import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import apiService from "../Services/api.service";
import CustomButton from "./Button";
import { useToast } from "./Toast";
export default function ChangePlanPopup({
  open,
  onClose,
  token,
  subscriptionId,
  plans = [],
  currentPlanId,
  selectedRegion,
}) {
  const Toast = useToast();
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [form, setForm] = useState({
    subscriptionId: "",
    newPlanId: "",
    reason: "",
  });
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      subscriptionId: subscriptionId,
    }));
  }, [subscriptionId]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  const handlePlanSelect = async (planId) => {
    handleChange("newPlanId", planId);

    if (!planId) return;

    try {
      setLoadingPreview(true);
      const r = await apiService.previewPlanChange(
        token,
        subscriptionId,
        planId
      );
      setPreview(r.data.data); // store preview data
    } catch (err) {
      console.log("Error loading preview", err);
      Toast.error(
        err.response?.data?.message || "Failed to load plan preview!"
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleCreate = async () => {
    try {
      const r = await apiService.changePlan(token, form);
      onClose();
    } catch (error) {
      console.log("Error in create subscription", error);
      Toast.error(
        error.response?.data?.message ||
          "An error occurred while changing subscription plan!"
      );
    }
  };
  // Change Plan is upgrade-only (downgrades go through Renew instead), so a
  // lower-tier plan isn't even offered here rather than being pickable and
  // then refused after the preview call comes back. The current plan itself
  // is excluded too — "you are already on this plan" isn't an upgrade choice.
  //
  // Compared by the current plan's own CATALOG price (looked up here from
  // the same `plans` list, not the subscription's possibly-discounted
  // customPrice/planSnapshot) — a heavily discounted "Plus" subscription is
  // still tier-wise above "Starter", and must never offer it as an upgrade.
  const currentPlanCatalogPrice = plans
    .find((p) => String(p._id) === String(currentPlanId))
    ?.geoPricing?.find((g) => g.geo === selectedRegion)?.price;

  const upgradablePlans = plans.filter((p) => {
    if (currentPlanId && String(p._id) === String(currentPlanId)) return false;
    if (currentPlanCatalogPrice == null) return true;
    const regionPrice = p.geoPricing?.find((g) => g.geo === selectedRegion);
    return (regionPrice?.price ?? 0) > currentPlanCatalogPrice;
  });

  const selectedPlanPricing = plans
    ?.find((p) => p._id === form.newPlanId)
    ?.geoPricing?.find((g) => g.geo === selectedRegion);

  const currency = selectedPlanPricing?.currency || "₹";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
    <DialogTitle
      sx={{
        fontWeight: "bold",
        fontSize: 20,
        pb: 1,
        borderBottom: "1px solid #eee",
      }}
    >
      Change Subscription Plan
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", right: 10, top: 10 }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
  
    <DialogContent dividers >
      <Grid container spacing={2}>
  
        {/* Select Plan */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            label="Select a New Plan"
            variant="outlined"
            value={form.newPlanId}
            onChange={(e) => handlePlanSelect(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          >
            {upgradablePlans.map((p) => {
              const regionPrice = p.geoPricing.find(
                (g) => g.geo === selectedRegion
              );
              return (
                <MenuItem key={p._id} value={p._id}>
                  <b>{p.name}</b> — {p.billingCycle} —{" "}
                  {regionPrice
                    ? `${regionPrice.currency} ${regionPrice.price}`
                    : "N/A"}
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>
  
        {loadingPreview && (
          <Grid item xs={12}>
            <Typography
              sx={{ mt: 1, fontSize: 15, fontWeight: 500, textAlign: "center" }}
            >
              Loading preview…
            </Typography>
          </Grid>
        )}
  
        {/* Preview */}
        {preview && !loadingPreview && !preview.samePlan && (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px solid #dde3ff",
                backgroundColor: "#f5f7ff",
              }}
            >
              {/* Title */}
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, mb: 2, color: "#2a2d65" }}
              >
                Plan Change Summary
              </Typography>
  
              {/* Badges */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item>
                  <Typography
                    sx={{
                      px: 1.6,
                      py: 0.6,
                      fontSize: 13,
                      borderRadius: 2,
                      backgroundColor: "#e5ecff",
                      color: "#1c2d6b",
                      fontWeight: 600,
                    }}
                  >
                    Billing: {preview.billingCycle}
                  </Typography>
                </Grid>
  
                <Grid item>
                  <Typography
                    sx={{
                      px: 1.6,
                      py: 0.6,
                      fontSize: 13,
                      borderRadius: 2,
                      backgroundColor:
                        preview.upgradeType === "payable"
                          ? "#fff4e5"
                          : preview.upgradeType === "refundable"
                          ? "#e8f7ed"
                          : "#e0e0e0",
                      color:
                        preview.upgradeType === "payable"
                          ? "#b85900"
                          : preview.upgradeType === "refundable"
                          ? "#1d7d3a"
                          : "#333",
                      fontWeight: 700,
                    }}
                  >
                    {preview.upgradeType.toUpperCase()}
                  </Typography>
                </Grid>
              </Grid>
  
              {/* Details */}
              <Grid container spacing={2} sx={{ fontSize: 14, lineHeight: 1.4,color: "#000"  }}>
                {[
                  ["Days Used", preview.daysUsed],
                  ["Days Remaining", preview.daysRemaining],
                  ["Old Full Price", `${currency} ${preview.OLD_FULL_PRICE}`],
                  ["New Full Price", `${currency} ${preview.NEW_FULL_PRICE}`],
                  [
                    "Remaining Old Value",
                    `${currency} ${preview.remainingOldValue}`,
                  ],
                  [
                    "New Remaining Value",
                    `${currency} ${preview.newRemainingValue}`,
                  ],
                  [
                    "Due for Previous Plan",
                    `${currency} ${preview.shortfallOnUsed}`,
                  ],
                  ["Over Paid", `${currency} ${preview.overpaid}`],
                ].map(([label, value], i) => (
                  <Grid item xs={6} key={i}>
                    <Typography >
                      {label}: <b>{value}</b>
                    </Typography>
                  </Grid>
                ))}
              </Grid>
  
              {/* Amount Card */}
              <Paper
                elevation={0}
                sx={{
                  mt: 3,
                  p: 2.5,
                  borderRadius: 3,
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e2e2",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 15,
                    color: "#666",
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                >
                  Amount to Collect
                </Typography>
  
                <Typography
                  sx={{
                    fontSize: 28,
                    fontWeight: 800,
                    color: preview.collectFinal > 0 ? "#d62828" : "#1b7f3b",
                  }}
                >
                  {currency} {preview.collectFinal}
                </Typography>
  
                {preview.refund > 0 && (
                  <Typography
                    sx={{
                      mt: 2,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      fontSize: 15,
                      fontWeight: 600,
                      display: "inline-block",
                      color: "#1b7f3b",
                      backgroundColor: "#eaf7ee",
                      border: "1px solid #ccefd8",
                    }}
                  >
                    Refund to User: {currency} {preview.refund}
                  </Typography>
                )}
              </Paper>
            </Paper>
          </Grid>
        )}
  
        {/* Same Plan Warning */}
        {preview?.samePlan && (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px solid #ffd6d6",
                backgroundColor: "#fff5f5",
              }}
            >
              <Typography sx={{ color: "#c62828", fontWeight: 700 }}>
                You are already on this plan.
              </Typography>
              <Typography sx={{ color: "#a80000", mt: 1 }}>
                Please pick a different plan to continue.
              </Typography>
            </Paper>
          </Grid>
        )}
  
        {/* Reason */}
        <Grid item xs={12}>
          <TextField
            label="Reason for Change"
            fullWidth
            required
            value={form.reason}
            onChange={(e) => handleChange("reason", e.target.value)}
            multiline
            minRows={2}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </Grid>
      </Grid>
    </DialogContent>
  
    {/* Actions */}
    <DialogActions sx={{ p: 2.5, borderTop: "1px solid #eee" }}>
      <CustomButton
        text="Cancel"
        variant="outlined"
        onClick={onClose}
        width="120px"
      />
      <CustomButton
        text="Confirm Change"
        onClick={handleCreate} 
        disabled={!form.newPlanId || !form.reason || !preview}
      />
    </DialogActions>
  </Dialog>
  
  );
}
